import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
  Paper,
  IconButton,
  Chip,
  useTheme,
  Tooltip,
  Grid,
  Pagination,
  InputLabel,
  Input,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import { tokens } from "../theme";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useUser } from "../provider/AuthProvider";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
const PAGE_SIZE = 5;

const CMTAnnouncementPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { t, i18n } = useTranslation();
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    title: "",
    message: "",
    imageUrl: "",
    startDate: "",
    endDate: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchStart, setSearchStart] = useState("");
  const [searchEnd, setSearchEnd] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { user } = useUser();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const fetchAnnouncements = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/news/getAll/${user.id}`
      );
      setAnnouncements(res.data);
      setFilteredAnnouncements(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleSave = async () => {
    let imageUrl = form.imageUrl;
    setIsSubmit(true);

    if (!form.startDate) {
      setSnackbar({
        open: true,
        message: t("announcement_page.error_start_date_required"),
        severity: "error",
      });
      setIsSubmit(false);
      return;
    }
    if (!form.endDate) {
      setSnackbar({
        open: true,
        message: t("announcement_page.error_end_date_required"),
        severity: "error",
      });
      setIsSubmit(false);
      return;
    }
    if (
      form.startDate &&
      form.endDate &&
      dayjs(form.startDate).isAfter(dayjs(form.endDate))
    ) {
      setSnackbar({
        open: true,
        message: t("announcement_page.error_end_before_start"),
        severity: "error",
      });
      setIsSubmit(false);
      return;
    }
    if (
      form.startDate &&
      dayjs(form.startDate).isBefore(dayjs().startOf("day"))
    ) {
      setSnackbar({
        open: true,
        message: t("announcement_page.error_start_in_past"),
        severity: "error",
      });
      setIsSubmit(false);
      return;
    }
    try {
      if (selectedImage) {
        const formData = new FormData();
        formData.append("file", selectedImage);
        const uploadRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/upload/user/photo`,
          formData
        );
        imageUrl = uploadRes.data;
      }
      const payload = {
        ...form,
        imageUrl,
        startDate: new Date(form.startDate),
        endDate: new Date(form.endDate),
        userId: user.id,
      };

      if (editingId) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/news/update`, {
          newsId: editingId,
          ...payload,
        });
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/news/create`,
          payload
        );
      }

      setOpenDialog(false);
      setForm({
        title: "",
        message: "",
        imageUrl: "",
        startDate: "",
        endDate: "",
      });
      setEditingId(null);
      setSelectedImage(null);
      setImagePreview(null);
      fetchAnnouncements();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setIsSubmit(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t("announcement_page.confirm_delete"))) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/news/delete/${id}`
        );
        fetchAnnouncements();
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: "error" });
      }
    }
  };

  const handleEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title,
      message: a.message,
      imageUrl: a.imageUrl || "",
      startDate: a.startDate.split("T")[0],
      endDate: a.endDate.split("T")[0],
    });
    setImagePreview(a.imageUrl || null);
    setOpenDialog(true);
  };
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const filterAnnouncements = () => {
    const filtered = announcements.filter((a) => {
      const matchesTitle = a.title
        .toLowerCase()
        .includes(searchTitle.toLowerCase());
      const matchesStart = searchStart
        ? dayjs(a.startDate).isAfter(dayjs(searchStart).subtract(1, "day"))
        : true;
      const matchesEnd = searchEnd
        ? dayjs(a.endDate).isBefore(dayjs(searchEnd).add(1, "day"))
        : true;
      return matchesTitle && matchesStart && matchesEnd;
    });
    setFilteredAnnouncements(filtered);
    setPage(1);
  };
  useEffect(() => {
    filterAnnouncements();
  }, [searchTitle, searchStart, searchEnd, announcements]);
  const paginatedAnnouncements = announcements.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const calculateRemainingDays = (endDate) => {
    const now = dayjs();
    const end = dayjs(endDate);
    const diff = end.diff(now, "day");
    return diff >= 0 ? t("announcement_page.days_left", { count: diff }) : t("announcement_page.expired");
  };

  const handleEventDrop = async (info) => {
    const announcement = announcements.find(
      (a) => a.title === info.event.title
    );
    if (!announcement) return;

    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/news/update`, {
        newsId: announcement.id,
        title: announcement.title,
        message: announcement.message,
        imageUrl: announcement.imageUrl,
        startDate: info.event.start,
        endDate: info.event.end || info.event.start,
        userId: user.id,
      });
      fetchAnnouncements();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setSelectedImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box px={3} py={4}>
      <BreadcrumbsBar />
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress size="50px" />
        </Box>
      ) : (
        <Box>
          <Grid
            container
            spacing={4}
            columns={{ xs: 2, sm: 8, md: 10 }}
            sx={{
              width: "100%",
              height: "100%",
            }}
          >
            <Grid item size={{ xs: 2, sm: 4, md: 5 }}>
              <Paper
                sx={{
                  padding: 2,
                  height: "80vh",
                  display: "flex",
                  flexDirection: "column",
                }}
                elevation={3}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Box mb={2}>
                    <TextField
                      label={t("announcement_page.search_title")}
                      value={searchTitle}
                      onChange={(e) => setSearchTitle(e.target.value)}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
                        >
                          <DatePicker
                            label={
                              i18n.language === "ar"
                                ? "تاريخ البدء"
                                : "Start Date"
                            }
                            value={searchStart ? dayjs(searchStart) : null}
                            onChange={(newValue) =>
                              setSearchStart(
                                newValue ? newValue.format("YYYY-MM-DD") : ""
                              )
                            }
                            // format=" " // ✅ Hides "mm/dd/yyyy"
                            slotProps={{
                              textField: {
                                placeholder: "", // ✅ Hide placeholder
                                fullWidth: true,
                              },
                            }}
                          />
                        </LocalizationProvider>
                        {/* <TextField
                          label="Start Date"
                          type="date"
                          value={searchStart}
                          onChange={(e) => setSearchStart(e.target.value)}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            placeholder: "", // ✅ Custom format
                          }}
                        /> */}
                      </Grid>
                      <Grid item xs={6}>
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
                        >
                          <DatePicker
                            label={
                              i18n.language === "ar"
                                ? "تاريخ الانتهاء"
                                : "End Date"
                            }
                            value={searchEnd ? dayjs(searchEnd) : null}
                            onChange={(newValue) =>
                              setSearchEnd(
                                newValue ? newValue.format("YYYY-MM-DD") : ""
                              )
                            }
                            slotProps={{
                              textField: {
                                placeholder: "", // ✅ Hide placeholder
                                fullWidth: true,
                              },
                            }}
                          />
                        </LocalizationProvider>
                        {/* <TextField
                          label="End Date"
                          type="date"
                          value={searchEnd}
                          onChange={(e) => setSearchEnd(e.target.value)}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        /> */}
                      </Grid>
                    </Grid>
                  </Box>
                  <Button
                    startIcon={<Add />}
                    variant="contained"
                    size="medium"
                    onClick={() => {
                      setOpenDialog(true);
                      setForm({
                        title: "",
                        message: "",
                        imageUrl: "",
                        startDate: "",
                        endDate: "",
                      });
                    }}
                  >
                    {t("announcement_page.new_announcement")}
                  </Button>
                </Box>
                <Box
                  sx={{
                    overflowY: "auto",
                    flexGrow: 1,
                    pr: 1,
                  }}
                >
                  <Stack spacing={3}>
                    {filteredAnnouncements.map((a) => (
                      <Paper
                        key={a.id}
                        elevation={10}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          backgroundColor: theme.palette.background.paper,
                          boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                          transition: "transform 0.2s ease",
                          "&:hover": { transform: "scale(1.01)" },
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              color="text.primary"
                              className="notranslate"
                            >
                              {a.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              mt={1}
                              color="text.secondary"
                              className="notranslate"
                            >
                              {a.message}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mt={1}
                              className="notranslate"
                            >
                              {dayjs(a.startDate).format("MMM D")} –{" "}
                              {dayjs(a.endDate).format("MMM D, YYYY")}
                            </Typography>
                            <Stack direction="row" spacing={1} mt={1}>
                              <Chip
                                label={a.isActived ? t("announcement_page.inactive") : t("announcement_page.active")}
                                color={a.isActived ? "default" : "success"}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                              <Chip
                                label={calculateRemainingDays(a.endDate)}
                                color={
                                  dayjs(a.endDate).diff(dayjs(), "day") < 0
                                    ? "error"
                                    : "info"
                                }
                                size="small"
                              />
                            </Stack>
                          </Box>
                          <Stack>
                            {a.imageUrl && (
                              <img
                                src={a.imageUrl}
                                alt="announcement"
                                width={120}
                                style={{ marginTop: 10, borderRadius: 8 }}
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title={t("announcement_page.edit")}>
                              <IconButton
                                onClick={() => handleEdit(a)}
                                color="info"
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("announcement_page.delete")}>
                              <IconButton
                                onClick={() => handleDelete(a.id)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                </Box>

                <Box mt={3} display="flex" justifyContent="center">
                  <Pagination
                    count={Math.ceil(announcements.length / PAGE_SIZE)}
                    page={page}
                    onChange={handlePageChange}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item size={{ xs: 2, sm: 4, md: 5 }}>
              <Paper
                sx={{
                  padding: 2,
                  height: "80vh",
                }}
                elevation={3}
              >
                <Typography variant="h6" mb={2}>
                  {t("announcement_page.announcement_schedule")}
                </Typography>
                <div className="notranslate">
                  <FullCalendar
                    locale={i18n.language}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                      left: "prev, next",
                      center: "title",
                      right: "",
                    }}
                    editable={true}
                    eventDrop={handleEventDrop}
                    events={announcements.map((a) => {
                      const today = dayjs();
                      const start = dayjs(a.startDate);
                      const end = dayjs(a.endDate);

                      let backgroundColor = "#66bb6a"; // default to Active (green)

                      if (a.isActived) {
                        backgroundColor = "#9e9e9e"; // inactive
                      } else if (end.isBefore(today, "day")) {
                        backgroundColor = "#ef5350"; // expired
                      } else if (start.isAfter(today, "day")) {
                        backgroundColor = "#42a5f5"; // upcoming
                      }

                      return {
                        title: a.title,
                        start: a.startDate,
                        end: a.endDate,
                        backgroundColor,
                        borderColor: "transparent",
                        textColor: "#fff",
                        extendedProps: {
                          message: a.message,
                        },
                      };
                    })}
                    eventDidMount={(info) => {
                      info.el.style.borderRadius = "8px";
                      info.el.style.padding = "6px 8px";
                      info.el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                      info.el.style.fontWeight = "500";
                      info.el.style.fontSize = "0.875rem";
                      info.el.style.fontFamily = '"Roboto", sans-serif';

                      info.el.onmouseenter = () => {
                        info.el.style.transform = "scale(1.02)";
                        info.el.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                      };
                      info.el.onmouseleave = () => {
                        info.el.style.transform = "scale(1)";
                        info.el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                      };
                    }}
                    dateClick={(info) => {
                      setForm({
                        ...form,
                        startDate: info.dateStr,
                        endDate: info.dateStr,
                      });
                      setOpenDialog(true);
                    }}
                    eventClick={(info) => {
                      const a = announcements.find(
                        (x) => x.title === info.event.title
                      );
                      if (a) handleEdit(a);
                    }}
                  />
                </div>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingId ? t("announcement_page.edit_announcement") : t("announcement_page.create_announcement")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label={t("announcement_page.title")}
              variant="outlined"
              fullWidth
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              label={t("announcement_page.message")}
              fullWidth
              multiline
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t("announcement_page.upload_image")}
              </Typography>
              <Button variant="outlined" component="label" sx={{ mb: 2 }}>
                {t("announcement_page.select_image")}
                <input type="file" hidden onChange={handleImageChange} />
              </Button>

              {imagePreview && (
                <Box display="flex" justifyContent="center">
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      width: 180,
                      height: 180,
                      objectFit: "cover",
                      borderRadius: 2,
                      border: "1px solid #ccc",
                      boxShadow: 2,
                    }}
                  />
                </Box>
              )}
            </Box>
            <Grid container spacing={5} columns={{ xs: 2, sm: 8, md: 10 }}>
              <Grid item size={{ xs: 2, sm: 4, md: 5 }}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
                >
                  <DatePicker
                    className="notranslate"
                    label={
                      i18n.language === "ar" ? "تاريخ البدء" : "Start Date"
                    }
                    value={form.startDate ? dayjs(form.startDate) : null}
                    onChange={(newValue) =>
                      setForm({
                        ...form,
                        startDate: newValue
                          ? newValue.format("YYYY-MM-DD")
                          : "",
                      })
                    }
                    // format="mm/dd/yyyy" // ✅ Hides "mm/dd/yyyy"
                    slotProps={{
                      textField: {
                        placeholder: "", // ✅ Hide placeholder
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
                {/* <TextField
                  type="date"
                  label="Start Date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                /> */}
              </Grid>
              <Grid item size={{ xs: 2, sm: 4, md: 5 }}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
                >
                  <DatePicker
                    className="notranslate"
                    label={
                      i18n.language === "ar" ? "تاريخ البدء" : "Start Date"
                    }
                    value={form.endDate ? dayjs(form.endDate) : null}
                    onChange={(newValue) =>
                      setForm({
                        ...form,
                        endDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                      })
                    }
                    // format="mm/dd/yyyy" // ✅ Hides "mm/dd/yyyy"
                    slotProps={{
                      textField: {
                        placeholder: "", // ✅ Hide placeholder
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
                {/* <TextField
                  type="date"
                  label="End Date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.endDate}
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                /> */}
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          {isSubmit ? (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSubmit}
            >
              {editingId ? t("announcement_page.updating") : t("announcement_page.creating")}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSave}>
              {editingId ? t("announcement_page.update") : t("announcement_page.create")}
            </Button>
          )}

          <Button onClick={() => setOpenDialog(false)}>{t("announcement_page.cancel")}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CMTAnnouncementPage;
